import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import TopBar from "@/components/TopBar";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/blog/posts")
      .then((res) => setPosts(res.data || []))
      .catch((e) => setError(e?.message || "Failed to load posts"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-list-page">
      <SEO
        title="HVAC Blog, Corona Gas | Heat Pump Rebates, Furnace Tips & More"
        description="Read expert articles from Corona Gas on HVAC maintenance, CleanBC rebates, heat pumps, furnaces, gas fitting, and home comfort in BC."
        url="https://coronagas.ca/blog"
      />
      <TopBar />
      <Navbar />

      {/* Page header */}
      <header className="relative cg-pink-bg py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 cg-halftone-light opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-[#E50914] opacity-10 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div className="eyebrow justify-center inline-flex mb-4">Insights & Guides</div>
          <h1 className="font-[Manrope] font-extrabold text-[#0F0F0F] text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
            The Corona Gas <span className="text-[#E50914]">Blog</span>
          </h1>
          <p className="text-[#475569] text-lg max-w-2xl mx-auto">
            Expert articles on HVAC maintenance, energy rebates, and keeping your BC home comfortable year-round.
          </p>
        </div>
      </header>

      {/* Posts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {loading && (
            <div className="flex justify-center py-20" data-testid="blog-loading">
              <Loader2 className="w-8 h-8 animate-spin text-[#E50914]" />
            </div>
          )}
          {error && (
            <div className="text-center text-red-500 py-10" data-testid="blog-error">
              {error}
            </div>
          )}
          {!loading && !error && posts.length === 0 && (
            <div className="text-center py-20 text-[#475569]">
              No posts yet, check back soon.
            </div>
          )}
          {!loading && posts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <article
                  key={p.id}
                  className="service-card flex flex-col"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link to={`/blog/${p.slug}`} className="block">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                      {p.tags && p.tags[0] && (
                        <span className="absolute top-3 left-3 bg-[#E50914] text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {p.tags[0]}
                        </span>
                      )}
                    </div>
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-xs text-[#64748B] mb-3">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(p.published_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        {p.read_time} min read
                      </span>
                    </div>
                    <h2 className="font-[Manrope] font-bold text-[#0F0F0F] text-xl mb-3 leading-snug">
                      <Link to={`/blog/${p.slug}`} className="hover:text-[#E50914]">
                        {p.title}
                      </Link>
                    </h2>
                    <p className="text-[#475569] text-sm leading-relaxed mb-4 flex-1">
                      {p.excerpt}
                    </p>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="inline-flex items-center gap-1.5 text-[#E50914] font-semibold text-sm hover:gap-3 transition-all"
                      data-testid={`blog-read-${p.slug}`}
                    >
                      Read article
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListPage;
